/**
 * The redline as it was drafted, seen from one author's side: which of its
 * paragraphs edit existing text (and what that text was before this author
 * touched it) and which are new paragraphs to insert whole.
 *
 * A redline arrives in one of two shapes (`RedlinePayload`). As OOXML carrying
 * tracked changes by its author, the shape prod's writer produces: the source
 * reads two strings per paragraph out of it. Or as those strings already, the
 * shape the core emits when the proposal engine flag is on: the source takes
 * them as they are. Either way the builder gets the same three things per
 * edited paragraph: the text the redline wants (`allChangesAcceptedTexts`), the text it
 * was written against (`textsBeforeAuthor`, the "original" of the three-way
 * merge), and, separately, the paragraphs that have no live counterpart at
 * all and are inserted whole (`insertions`).
 */
import { OoxmlRange } from "./ooxml-range";
import { ParagraphModel } from "./ooxml/paragraph-model";
import { ParagraphXml } from "./ooxml/paragraph-xml";
import { WordTokenizer } from "./text/word-tokenizer";
import { carriesTableOperations, type ParagraphListContinuation, type RedlinePayload, type RedlineTextProposal } from "./wire/redline-payload";
import { type RedlinePlacement, relocatesBesideParagraph } from "./wire/redline-placement";

/**
 * True when a redline changes a paragraph's text: its text before and after
 * the redline differ once each is read by `WordTokenizer.comparisonKey`, so a
 * word stored in another Unicode form reads as the same word.
 *
 * EXAMPLE
 *   "5. Payment"              -> "5. Payment"               false
 *   "5. Payment"              -> "5. Payment terms"         true
 *   "Résumé" (é as U+00E9)    -> "Résumé" (e + U+0301)      false
 */
const redlineChangesText = (textBeforeRedline: string, textAfterRedline: string): boolean =>
    WordTokenizer.comparisonKey(textBeforeRedline) !== WordTokenizer.comparisonKey(textAfterRedline);

/**
 * True when a redline paragraph is written although its live paragraph is gone from
 * the document: placed beside a paragraph (`after_paragraph` / `before_paragraph`), or
 * written back where the user deleted it (`RedlineSource.placedOnPageParagraphs`).
 *
 * CASES
 *   - it changes the paragraph's text and leaves a word in it -> written: the card's words
 *   - it leaves the paragraph as it was                        -> not written: the paragraph was
 *                                                                 deleted, and writing it would put
 *                                                                 it back
 *   - it empties the paragraph (a whole-paragraph deletion)    -> not written: the user already
 *                                                                 deleted it, so the card's change
 *                                                                 is done, and writing it would add
 *                                                                 an empty paragraph
 *
 * EXAMPLE
 *   "The term is one year." -> "The term is two years."   true
 *   "The term is one year." -> "The term is one year."    false
 *   "The term is one year." -> ""                          false
 */
export const paragraphIsWrittenWhereGone = (textBeforeRedline: string, textAfterRedline: string): boolean =>
    redlineChangesText(textBeforeRedline, textAfterRedline) && WordTokenizer.words(textAfterRedline).length > 0;

/**
 * True when a redline writes nothing once every paragraph it names is gone from the
 * document: each of its paragraphs it only deletes, or leaves as it was
 * (`paragraphIsWrittenWhereGone`), and it adds no paragraph, row or table.
 *
 * WHY
 *   Such a card is done when the user already deleted its paragraphs: there is
 *   nothing of it left to write, and finding "its paragraph" again can only find
 *   another one. A server relocation once put a card that deleted 7.2 into the
 *   paragraph that quoted 7.2, and deleted that (live run of #15, step 4,
 *   2026-10-18).
 *
 * CASES
 *   - a card that deletes 7.2 whole                        -> true
 *   - a card that rewrites 7.2                             -> false: its words go back (`placedOnPageParagraphs`,
 *                                                             or recovery placing it beside a paragraph)
 *   - a card that deletes 7.2 and adds 7.3 after it        -> false: 7.3 is written
 *   - a card with a row or table operation                 -> false: the operation is written
 */
export function redlineWritesNothingWhereItsParagraphsAreGone(redline: RedlinePayload): boolean {
    /** WHEN the card carries a row or table operation CONSEQUENCE that operation is still written. */
    if (redline.kind === "texts" && carriesTableOperations(redline.tableChanges)) return false;
    return RedlineSource.fromPayload(redline).writesNothingWhereItsParagraphsAreGone();
}

/** A redline paragraph with no live counterpart, inserted whole after desired paragraph `afterDesiredIndex` (-1 for the front). */
export interface ParagraphInsertion {
    afterDesiredIndex: number;
    /**
     * Whether a bare paragraph takes the properties of the live paragraph it follows
     * ("continue", one more list item) or stays plain ("none"). A paragraph that carries
     * its own properties is left as it is either way.
     */
    list: ParagraphListContinuation;
    paragraphXml: string;
    /**
     * The bookmark the inserted paragraph carries, so every reader that finds it by its
     * bookmark (the run-end check, the card's tracker, a second Apply, a comment the card
     * carries, Go to issue) finds the written paragraph.
     *
     * CASES
     *   - a paragraph the card adds              -> the id it was proposed with (`RedlineTextInsertion.bookmark`)
     *   - a card paragraph the user deleted, the
     *     card writing it back in its place      -> the card paragraph's own (`RedlineSource.placedOnPageParagraphs`)
     *
     * NULL, or absent
     *   A paragraph the card adds that was proposed before new paragraphs carried an id,
     *   or a redline placed beside a paragraph: its bookmarks sit on the live paragraph
     *   and are moved after the write (`ProposalApplier`).
     */
    carriedBookmarkName?: string | null;
}

/** The redline's paragraphs as the builder consumes them: per edited paragraph the wanted and the original text, plus whole insertions. */
export interface PlacedRedline {
    allChangesAcceptedTexts: string[];
    textsBeforeAuthor: string[];
    insertions: ParagraphInsertion[];
}

/** What a redline is placed for: the placement its tracker holds, and the page it is built over. */
export interface RedlinePlacementRequest {
    placement: RedlinePlacement;
    /** The page's paragraphs, every change accepted, in order. */
    textsOnPage: string[];
    /** Every bookmark whose start the page carries (`OoxmlRange.bookmarkNamesCarried`). */
    bookmarkNamesOnPage: ReadonlySet<string>;
    /**
     * How many of the page's paragraphs, from the first, are the document's: a page that is
     * Word's export as given ends with Word's empty paragraph, which no card is placed after
     * (`OoxmlRange.paragraphCountBeforeWordsClosingParagraph`).
     *
     * NULL
     *   Absent on a page that holds the document's paragraphs alone: every one of them.
     */
    documentParagraphCountOnPage?: number;
}

interface RedlineSourceParts extends PlacedRedline {
    author: string;
    /** The redline's paragraphs as plain accepted markup, used when the whole redline is placed beside a paragraph as new paragraphs. */
    besideParagraphXmls: string[];
    /**
     * For each paragraph of `besideParagraphXmls`, in order, whether it is written when
     * its live paragraph is gone (`paragraphIsWrittenWhereGone`).
     */
    writtenWhereParagraphIsGone: boolean[];
    /**
     * For each redline paragraph, in order, the bookmark naming the live paragraph it
     * edits: a text card's own (`RedlineTextParagraph.bookmark`).
     *
     * CASES
     *   - a text card      -> one entry per card paragraph, null for a paragraph that carries none
     *   - a markup redline -> none: its paragraphs are found by the build's alignment alone
     */
    bookmarkByRedlineParagraph: (string | null)[];
    /**
     * The redline's new paragraphs that `besideParagraphXmls` does not already hold,
     * each anchored after the index of the redline paragraph it follows.
     *
     * CASES
     *   - a text card    -> its insertions: its paragraphs are listed apart from them
     *   - a markup redline -> none: its accepted paragraphs already include every
     *                         paragraph it inserts, in their place
     */
    besideInsertions: ParagraphInsertion[];
}

export class RedlineSource implements PlacedRedline {
    readonly allChangesAcceptedTexts: string[];
    readonly textsBeforeAuthor: string[];
    readonly insertions: ParagraphInsertion[];
    readonly author: string;
    readonly besideParagraphXmls: string[];
    private readonly writtenWhereParagraphIsGone: boolean[];
    private readonly bookmarkByRedlineParagraph: (string | null)[];
    private readonly besideInsertions: ParagraphInsertion[];

    private constructor(parts: RedlineSourceParts) {
        this.allChangesAcceptedTexts = parts.allChangesAcceptedTexts;
        this.textsBeforeAuthor = parts.textsBeforeAuthor;
        this.insertions = parts.insertions;
        this.author = parts.author;
        this.besideParagraphXmls = parts.besideParagraphXmls;
        this.writtenWhereParagraphIsGone = parts.writtenWhereParagraphIsGone;
        this.bookmarkByRedlineParagraph = parts.bookmarkByRedlineParagraph;
        this.besideInsertions = parts.besideInsertions;
    }

    /**
     * CASES
     *   - `ooxml` payload -> read the two text layers out of the markup
     *   - `texts` payload -> take the layers as given
     */
    static fromPayload(payload: RedlinePayload): RedlineSource {
        if (payload.kind === "ooxml") return RedlineSource.fromOoxml(payload.redlineOoxml, payload.author);
        return RedlineSource.fromTexts(payload);
    }

    /**
     * The text card's strings, one entry per paragraph of the bookmarked range
     * in order. A requested new paragraph goes after the card paragraph whose
     * bookmark it names; its markup is a plain paragraph of the text, which the
     * builder marks as an insertion when it places it.
     */
    static fromTexts(proposal: RedlineTextProposal): RedlineSource {
        const plainParagraphXml = (text: string): string => ParagraphXml.plainFromText({ text, templateParagraphXml: "" });
        const allChangesAcceptedTexts = proposal.paragraphs.map((paragraph) => paragraph.textProposed);
        const insertions = proposal.insertions.map((insertion) => {
            const anchorIndex = proposal.paragraphs.findIndex((paragraph) => paragraph.bookmark === insertion.afterBookmark);
            /**
             * CASES
             *   - the named paragraph is in the card -> insert after it
             *   - it is not                          -> insert after the card's last paragraph
             *
             * EXAMPLE
             *   `proposal.paragraphs.length` = 3
             *   `length - 1`                 -> 2, the last paragraph's index
             */
            const afterDesiredIndex = anchorIndex === -1 ? proposal.paragraphs.length - 1 : anchorIndex;
            /**
             * NULL `carriedBookmarkName` for a new paragraph proposed before new paragraphs
             * carried an id: it is written unbookmarked, as before.
             */
            return { afterDesiredIndex, list: insertion.list, paragraphXml: plainParagraphXml(insertion.textProposed), carriedBookmarkName: insertion.bookmark ?? null };
        });
        return new RedlineSource({
            allChangesAcceptedTexts,
            textsBeforeAuthor: proposal.paragraphs.map((paragraph) => paragraph.textWhenProposed),
            insertions,
            author: proposal.author,
            besideParagraphXmls: allChangesAcceptedTexts.map(plainParagraphXml),
            writtenWhereParagraphIsGone: proposal.paragraphs.map((paragraph) => paragraphIsWrittenWhereGone(paragraph.textWhenProposed, paragraph.textProposed)),
            bookmarkByRedlineParagraph: proposal.paragraphs.map((paragraph) => paragraph.bookmark),
            besideInsertions: insertions,
        });
    }

    /**
     * A paragraph is an insertion when nothing of it predates the author and
     * something of it survives acceptance. When every paragraph is an insertion
     * there is nothing to anchor to, so all are treated as edits of the live
     * paragraphs instead.
     */
    static fromOoxml(redlineOoxml: string, author: string): RedlineSource {
        const redlineRange = new OoxmlRange(redlineOoxml);
        const allChangesAcceptedTexts: string[] = [];
        const textsBeforeAuthor: string[] = [];
        const wholeParagraphInsertions: ParagraphInsertion[] = [];
        const allAccepted: string[] = [];
        const allBefore: string[] = [];
        /** One entry per redline paragraph, so it lines up with `acceptedParagraphXmls()`, which lists every paragraph. */
        const writtenWhereParagraphIsGone: boolean[] = [];
        for (let redlineParagraphIndex = 0; redlineParagraphIndex < redlineRange.paragraphCount; redlineParagraphIndex++) {
            const redlineParagraphModel = new ParagraphModel(redlineRange.paragraphElement(redlineParagraphIndex));
            const acceptedParagraphText = redlineParagraphModel.text;
            const paragraphTextBeforeAuthor = redlineParagraphModel.textBeforeAuthor(author);
            allAccepted.push(acceptedParagraphText);
            allBefore.push(paragraphTextBeforeAuthor);
            writtenWhereParagraphIsGone.push(paragraphIsWrittenWhereGone(paragraphTextBeforeAuthor, acceptedParagraphText));
            const isWhollyInserted = paragraphTextBeforeAuthor.trim().length === 0 && acceptedParagraphText.trim().length > 0;
            /**
             * WHEN a paragraph has accepted text but no pre-author text
             *   CONSEQUENCE
             *     Record it only in `wholeParagraphInsertions`: it has no live paragraph to
             *     merge against.
             */
            if (isWhollyInserted) {
                const insertionAfterDesiredIndex = allChangesAcceptedTexts.length - 1;
                const insertedParagraphXml = redlineRange.paragraphXml(redlineParagraphIndex);
                /**
                 * EXAMPLE
                 *   `allChangesAcceptedTexts` holds only earlier paragraphs that stayed paired with live
                 *   paragraphs.
                 *
                 *   kept so far             = 3
                 *   desired indexes         = 0, 1, 2
                 *   `allChangesAcceptedTexts.length - 1` -> 2
                 *   `afterDesiredIndex`     = insert after desired paragraph 2
                 *
                 *   kept so far             = 0
                 *   `allChangesAcceptedTexts.length - 1` -> -1
                 *   `afterDesiredIndex`     = insert before desired paragraph 0
                 */
                // A markup paragraph carries its own properties, which the builder keeps; "continue" names that.
                wholeParagraphInsertions.push({ afterDesiredIndex: insertionAfterDesiredIndex, list: "continue", paragraphXml: insertedParagraphXml });
                /**
                 * CONSEQUENCE
                 *   Skip the per-paragraph merge arrays. This paragraph is already recorded as a
                 *   whole-paragraph insertion.
                 */
                continue;
            }
            allChangesAcceptedTexts.push(acceptedParagraphText);
            textsBeforeAuthor.push(paragraphTextBeforeAuthor);
        }
        const besideParagraphXmls = redlineRange.acceptedParagraphXmls();
        /**
         * WHEN no redline paragraph stayed paired with a live paragraph
         *   CONSEQUENCE
         *     Treat the whole redline as paragraph-aligned instead of insertion-only:
         *     use `allAccepted` and `allBefore`, and clear `insertions`.
         */
        if (allChangesAcceptedTexts.length === 0) {
            return new RedlineSource({ allChangesAcceptedTexts: allAccepted, textsBeforeAuthor: allBefore, insertions: [], author, besideParagraphXmls, writtenWhereParagraphIsGone, bookmarkByRedlineParagraph: [], besideInsertions: [] });
        }
        return new RedlineSource({ allChangesAcceptedTexts, textsBeforeAuthor, insertions: wholeParagraphInsertions, author, besideParagraphXmls, writtenWhereParagraphIsGone, bookmarkByRedlineParagraph: [], besideInsertions: [] });
    }

    /** True when none of the redline's paragraphs is written where its own is gone (`paragraphIsWrittenWhereGone`) and it inserts no paragraph. */
    writesNothingWhereItsParagraphsAreGone(): boolean {
        return this.insertions.length === 0 && !this.writtenWhereParagraphIsGone.includes(true);
    }

    /**
     * The redline arranged for a placement over the page it is built on.
     *
     * CASES
     *   - placed beside a paragraph (`after_paragraph` / `before_paragraph`) -> `placedBesideParagraph`
     *   - laid on its paragraphs                                             -> `placedOnPageParagraphs`
     */
    placedFor({ placement, textsOnPage, bookmarkNamesOnPage, documentParagraphCountOnPage }: RedlinePlacementRequest): PlacedRedline {
        /** NULL `documentParagraphCountOnPage`: the page holds the document's paragraphs alone, every one counted. */
        if (relocatesBesideParagraph(placement)) return this.placedBesideParagraph(placement, textsOnPage, documentParagraphCountOnPage ?? textsOnPage.length);
        return this.placedOnPageParagraphs(bookmarkNamesOnPage);
    }

    /**
     * The redline laid on its paragraphs, with every card paragraph the user deleted
     * written back where it stood.
     *
     * CASES
     *   - the page holds every card paragraph's bookmark, or none of them -> the redline itself:
     *                                                                        every paragraph is
     *                                                                        laid by the build's
     *                                                                        alignment, as before
     *   - the page holds some of them                                     -> the ones it holds are
     *                                                                        laid on their paragraphs;
     *                                                                        each one it does not
     *                                                                        hold is gone from the
     *                                                                        document, and is written
     *                                                                        back as a new paragraph
     *                                                                        (`paragraphIsWrittenWhereGone`)
     *                                                                        right after the card
     *                                                                        paragraph before it that
     *                                                                        the page holds, or at the
     *                                                                        front when none is
     *
     * WHY none of them is the same as all of them
     *   A page that holds no bookmark of the card is not a read of the card's
     *   paragraphs: the preview built before Word was read lays plain paragraphs of
     *   the texts when proposed, and a markup redline names no bookmark. Such a page
     *   is laid by alignment as it always was.
     *
     * WHY written back whole, in the card's words, carrying its bookmark
     *   A card whose paragraph the user deleted by hand applies: the card's proposed
     *   paragraph goes back in as one tracked insertion the user can reject. Laid by
     *   alignment instead, its words were joined onto the
     *   paragraph before it: "…days of invoice.The term is two years." as one
     *   paragraph. The paragraph carries the card paragraph's bookmark, so the
     *   run-end check, the card's tracker and a second Apply find it as the card's.
     *
     * WHY right after the card paragraph before it
     *   A card's paragraphs are the consecutive paragraphs of its range, so the
     *   paragraph before a deleted one in the card stood right before it in the
     *   document; for the card's first paragraph, the card paragraph after it stood
     *   right after it.
     *
     * EXAMPLE
     *   card    [_pay "…thirty days…" -> "…forty-five days…", _term "The term is one year." -> "The term is two years."]
     *   page    holds _pay; _term deleted by the user
     *   -> laid      ["…forty-five days…"] on _pay
     *   -> inserted  "The term is two years." after it, carrying _term
     *   the same card, _term -> "" (the card deletes it)
     *   -> laid      ["…forty-five days…"] on _pay; nothing inserted: the card's deletion is done
     */
    private placedOnPageParagraphs(bookmarkNamesOnPage: ReadonlySet<string>): PlacedRedline {
        const goneFromPageByRedlineParagraph = this.bookmarkByRedlineParagraph.map((bookmarkName) => bookmarkName !== null && !bookmarkNamesOnPage.has(bookmarkName));
        const pageHoldsACardParagraph = this.bookmarkByRedlineParagraph.some((bookmarkName) => bookmarkName !== null && bookmarkNamesOnPage.has(bookmarkName));
        /** WHEN no card paragraph is gone, or the page is no read of the card's paragraphs CONSEQUENCE laid by alignment, as before. */
        if (!pageHoldsACardParagraph || !goneFromPageByRedlineParagraph.includes(true)) return this;
        const laidIndexAtOrBefore = RedlineSource.laidIndexAtOrBeforeEachParagraph(goneFromPageByRedlineParagraph);
        /**
         * The live index a paragraph placed after redline paragraph `redlineParagraphIndex` goes after.
         *
         * CASES
         *   - `redlineParagraphIndex` -1 (before every card paragraph) -> -1, the front
         *   - otherwise                                               -> the laid index at or before it
         */
        const laidIndexAfter = (redlineParagraphIndex: number): number => (redlineParagraphIndex < 0 ? -1 : laidIndexAtOrBefore[redlineParagraphIndex]);
        /** The card's own new paragraphs after redline paragraph `redlineParagraphIndex`, moved onto the laid index they now follow. */
        const cardInsertionsAfter = (redlineParagraphIndex: number): ParagraphInsertion[] =>
            this.insertions
                .filter((insertion) => insertion.afterDesiredIndex === redlineParagraphIndex)
                .map((insertion) => ({ ...insertion, afterDesiredIndex: laidIndexAfter(redlineParagraphIndex) }));
        /**
         * CASES, for redline paragraph `redlineParagraphIndex`
         *   - gone, and it writes words  -> [it], written back after the laid paragraph before it, carrying its bookmark
         *   - gone, and it writes none   -> []: unchanged, or a deletion the user already made
         *   - on the page                -> []: laid on its paragraph
         */
        const writtenBackParagraph = (redlineParagraphIndex: number): ParagraphInsertion[] =>
            goneFromPageByRedlineParagraph[redlineParagraphIndex] && this.writtenWhereParagraphIsGone[redlineParagraphIndex]
                ? [{ afterDesiredIndex: laidIndexAfter(redlineParagraphIndex), list: "continue", paragraphXml: this.besideParagraphXmls[redlineParagraphIndex], carriedBookmarkName: this.bookmarkByRedlineParagraph[redlineParagraphIndex] }]
                : [];
        /**
         * WHY one list in reading order
         *   A written-back paragraph and the card's new paragraph after it share a laid
         *   index, and the builder writes paragraphs that share an index in list order.
         */
        const insertionsInReadingOrder = [
            ...cardInsertionsAfter(-1),
            ...this.bookmarkByRedlineParagraph.flatMap((_bookmarkName, redlineParagraphIndex) => [...writtenBackParagraph(redlineParagraphIndex), ...cardInsertionsAfter(redlineParagraphIndex)]),
        ];
        const stayOnPage = (_paragraphText: string, redlineParagraphIndex: number): boolean => !goneFromPageByRedlineParagraph[redlineParagraphIndex];
        return {
            allChangesAcceptedTexts: this.allChangesAcceptedTexts.filter(stayOnPage),
            textsBeforeAuthor: this.textsBeforeAuthor.filter(stayOnPage),
            insertions: insertionsInReadingOrder,
        };
    }

    /**
     * For each redline paragraph, the index among the paragraphs laid on the page of
     * the last one at or before it that the page holds.
     *
     * EXAMPLE
     *   gone                 [false, true, false, true]
     *   laid count so far     1      1     2      2
     *   laid index - 1        0      0     1      1      (count - 1: the last laid paragraph's index)
     *   gone                 [true, false]
     *   laid count so far     0     1
     *   laid index - 1       -1     0                    (-1: no laid paragraph before it, the front)
     */
    private static laidIndexAtOrBeforeEachParagraph(goneFromPageByRedlineParagraph: boolean[]): number[] {
        let laidParagraphCount = 0;
        return goneFromPageByRedlineParagraph.map((goneFromPage) => {
            /** WHEN the paragraph stays on the page CONSEQUENCE it is laid, and counts. */
            if (!goneFromPage) laidParagraphCount += 1;
            return laidParagraphCount - 1;
        });
    }

    /**
     * The redline placed beside a paragraph. The live paragraphs are left alone
     * (they become both the wanted and the original text, so the engine changes
     * nothing in them) and the redline's paragraphs are inserted whole, as
     * accepted text, on the chosen side: every paragraph it writes where its own
     * is gone (`paragraphIsWrittenWhereGone`) and every paragraph it adds, each of
     * a text card's new paragraphs right after the card paragraph it follows, so
     * the card reads in its own order.
     *
     * EXAMPLE
     *   card ["5. Payment" unchanged] with "5.1 Late fees accrue." after it, placed after "4. Delivery"
     *   -> live "4. Delivery" left as it reads; inserted "5.1 Late fees accrue."
     *   card ["5. Payment" -> "5. Payment terms"] with the same new paragraph
     *   -> inserted "5. Payment terms", "5.1 Late fees accrue."
     */
    private placedBesideParagraph(placement: RedlinePlacement, textsOnPage: string[], documentParagraphCountOnPage: number): PlacedRedline {
        /**
         * CASES
         *   - `placement.kind === "after_paragraph"`  -> the page's last paragraph of the document: Word's
         *                                                closing empty one, which a read of Word's export as
         *                                                given still holds, is not counted
         *                                                (`RedlinePlacementRequest.documentParagraphCountOnPage`)
         *   - otherwise                               -> `-1`
         *
         * EXAMPLE
         *   textsOnPage             = ["Test text 7 (P1):"], 1 of them the document's
         *   after-paragraph index   = count 1 - 1 = 0
         *   meaning                 = insert after the label
         *
         *   textsOnPage             = ["7.9 Either party may renew.", ""], Word's export as given, 1 the document's
         *   after-paragraph index   = count 1 - 1 = 0
         *   meaning                 = insert after 7.9, before Word's empty paragraph
         *
         *   before-paragraph index  = -1
         *   meaning                 = insert before live paragraph 0
         */
        const afterDesiredIndex = placement.kind === "after_paragraph" ? documentParagraphCountOnPage - 1 : -1;
        /**
         * The card's own new paragraphs that follow its paragraph `redlineParagraphIndex`,
         * placed at the same live index as the card's paragraphs, keeping the list
         * continuation the card asked for.
         *
         * EXAMPLE
         *   insertion after card paragraph 0 ("5. Payment"), live index 3
         *   -> { afterDesiredIndex: 3, list as the card asked, its paragraph }
         *
         *   `redlineParagraphIndex` -1 -> new paragraphs anchored before every card paragraph,
         *   which a card with no paragraphs of its own holds
         */
        const besideInsertionsAfter = (redlineParagraphIndex: number): ParagraphInsertion[] =>
            this.besideInsertions
                .filter((insertion) => insertion.afterDesiredIndex === redlineParagraphIndex)
                .map((insertion) => ({ ...insertion, afterDesiredIndex }));
        /**
         * The redline's paragraph `redlineParagraphIndex` placed beside the live one.
         *
         * CASES
         *   - it writes words where its own paragraph is gone -> [it], continuing the live
         *                                                       paragraph's list, since
         *                                                       paragraphs placed beside a
         *                                                       live one read like it
         *   - it leaves the paragraph as it was, or empties it -> []: the paragraph was deleted
         */
        const placedRedlineParagraph = (paragraphXml: string, redlineParagraphIndex: number): ParagraphInsertion[] =>
            this.writtenWhereParagraphIsGone[redlineParagraphIndex] ? [{ afterDesiredIndex, list: "continue", paragraphXml }] : [];
        /**
         * WHY one list in reading order
         *   Every paragraph placed beside the live one shares its live index, and the
         *   builder writes paragraphs that share an index in list order.
         */
        const placedParagraphInsertions = [
            ...besideInsertionsAfter(-1),
            ...this.besideParagraphXmls.flatMap((paragraphXml, redlineParagraphIndex) => [
                ...placedRedlineParagraph(paragraphXml, redlineParagraphIndex),
                ...besideInsertionsAfter(redlineParagraphIndex),
            ]),
        ];
        return { allChangesAcceptedTexts: [...textsOnPage], textsBeforeAuthor: [...textsOnPage], insertions: placedParagraphInsertions };
    }
}
